import type { FC } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, IconButton, Slider, Typography } from '@mui/material';
import { PlayArrow, Pause, SkipPrevious, SkipNext } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import { theme } from '../../styles/theme';

const ANIMATION_INTERVAL_MS = 350;

interface Props {
  mode: 'nccp' | 'status';
}

export const HeatMapYearTimeline: FC<Props> = ({ mode }) => {
  const { t } = useTranslation();
  const { filterConfig, updateFilterOptions } = useHeatMapFilterOptions();

  // Select year range based on mode
  const yearMin =
    mode === 'nccp' ? filterConfig.nccpYearMin : filterConfig.statusYearMin;
  const yearMax =
    mode === 'nccp' ? filterConfig.nccpYearMax : filterConfig.statusYearMax;

  const hasInitialized = useRef(false);
  const [currentYear, setCurrentYear] = useState<number>(yearMax);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!hasInitialized.current && yearMax > 0) {
      setCurrentYear(yearMax);
      hasInitialized.current = true;
    }
  }, [yearMax]);

  useEffect(() => {
    if (!isPlaying) return;

    const intervalId = setInterval(() => {
      setCurrentYear(prev => {
        const next = prev + 1;
        if (next > yearMax) {
          setIsPlaying(false);
          return yearMax;
        }
        return next;
      });
    }, ANIMATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isPlaying, yearMax]);

  useEffect(() => {
    const filterValue = currentYear < yearMax ? currentYear : undefined;
    // Use the correct filter key based on mode
    if (mode === 'nccp') {
      updateFilterOptions({ nccpYearMax: filterValue });
    } else {
      updateFilterOptions({ statusYearMax: filterValue });
    }
  }, [currentYear, yearMax, updateFilterOptions, mode]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      if (currentYear >= yearMax) {
        setCurrentYear(yearMin);
      }
      setIsPlaying(true);
    }
  }, [isPlaying, currentYear, yearMax, yearMin]);

  const handleBackToStart = useCallback(() => {
    setIsPlaying(false);
    setCurrentYear(yearMin);
  }, [yearMin]);

  const handleSkipToEnd = useCallback(() => {
    setIsPlaying(false);
    setCurrentYear(yearMax);
  }, [yearMax]);

  const handleSliderChange = useCallback(
    (_: Event, value: number | number[]) => {
      setIsPlaying(false);
      if (typeof value === 'number') {
        setCurrentYear(value);
      }
    },
    [],
  );

  if (yearMin >= yearMax) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        px: 1.5,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <IconButton
        size="small"
        onClick={handleBackToStart}
        disabled={currentYear === yearMin}
        aria-label="Back to start"
      >
        <SkipPrevious fontSize="small" />
      </IconButton>

      <IconButton
        size="small"
        onClick={handlePlayPause}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        sx={{
          backgroundColor: isPlaying
            ? theme.colors['green-100']
            : 'transparent',
          color: isPlaying ? 'white' : 'inherit',
          '&:hover': {
            backgroundColor: isPlaying
              ? theme.colors['green-90']
              : 'action.hover',
          },
        }}
      >
        {isPlaying ? (
          <Pause fontSize="small" />
        ) : (
          <PlayArrow fontSize="small" />
        )}
      </IconButton>

      <Slider
        value={currentYear}
        min={yearMin}
        max={yearMax}
        step={1}
        onChange={handleSliderChange}
        aria-label="Year filter"
        valueLabelDisplay={isPlaying ? 'on' : 'auto'}
        sx={{
          width: theme.spacing(45),
          mx: 1.5,
          '& .MuiSlider-valueLabel': {
            top: theme.spacing(9),
            transform: 'scale(0)',
            transformOrigin: 'top center',
            '&.MuiSlider-valueLabelOpen': {
              transform: 'scale(1)',
            },
            '&::before': {
              top: 0,
              bottom: 'unset',
              transform: 'translate(-50%, -50%) rotate(45deg)',
            },
          },
        }}
      />

      <IconButton
        size="small"
        onClick={handleSkipToEnd}
        disabled={currentYear === yearMax}
        aria-label="Skip to end"
      >
        <SkipNext fontSize="small" />
      </IconButton>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          whiteSpace: 'nowrap',
          ml: 0.5,
        }}
      >
        {mode === 'nccp'
          ? t('heatMapExperience.timeline.yearRange', {
              startYear: yearMin,
              endYear: currentYear,
            })
          : t('heatMapExperience.timeline.statusAsOf', {
              year: currentYear,
            })}
      </Typography>
    </Paper>
  );
};
